import { discoveryApiRef, fetchApiRef, useApi } from "@backstage/core-plugin-api";
import {
	APP_COMMIT,
	DEPLOY_PLUGIN_ID,
	DEPLOY_STATUS_PATH,
	type DeployStatusResponse,
} from "@internal/backstage-plugin-deploy-common";
import { useEffect, useState } from "react";

// The live half of the sidebar's commit indicator: it asks /api/deploy/status whether an update is
// on its way, so a colleague who sees the app is about to change is told so instead of noticing a
// changed sidebar hash after the fact.
//
// FAIL CLOSED-TO-QUIET, deliberately the mirror image of My AVD's fail-open: every error maps to
// `current`, which renders exactly the plain commit item we shipped before this existed. This
// decoration must never be able to damage a sidebar that is on every page — a 500, a missing
// backend plugin or a GitHub outage costs nothing at all.

/** Re-poll cadence. See the note on hidden tabs in {@link useDeployIndicator}. */
const POLL_INTERVAL_MS = 60_000;

/**
 * What the sidebar item should say about itself.
 *
 * - `current` — this bundle is the running version and nothing is shipping. Also the fail-quiet state.
 * - `rolling-out` — cd.yaml is building/deploying a newer commit right now.
 * - `awaiting-approval` — a newer commit is built and parked on the `prod` approval gate (ADR-0016).
 *   Its own state rather than a spinner, because it waits for a person and can last hours.
 * - `stale` — the running SERVER is a different commit from this bundle, i.e. the tab is old. Terminal
 *   until the page reloads, so the hook stops polling once it gets here.
 */
export type DeployIndicator = "current" | "rolling-out" | "awaiting-approval" | "stale";

/**
 * Turns one response into an indicator.
 *
 * `stale` OUTRANKS the two in-flight phases. Both can be true at once (a deploy landed and the next
 * one is already building), and of the two only `stale` is something the person can act on.
 *
 * Both commits must be non-empty to conclude anything: a local dev backend reports `""`, and
 * comparing that against a stamped bundle — or vice versa — would claim every dev session is stale.
 */
export function classifyDeployStatus(body: DeployStatusResponse): DeployIndicator {
	if (body.serverCommit && APP_COMMIT && body.serverCommit !== APP_COMMIT) {
		return "stale";
	}
	if (body.phase === "rolling-out" || body.phase === "awaiting-approval") {
		return body.phase;
	}
	return "current";
}

/**
 * Polls the rollout state while the page is open.
 *
 * A HIDDEN TAB DOES NOT POLL. Unlike My AVD's hook this one has no terminal state to settle on, so
 * left unguarded every parked tab in the company would call this route forever. `document.hidden`
 * cuts that to the tabs somebody is actually looking at; the timer still ticks, so a tab picks the
 * state back up the moment it is foregrounded. (The backend also caches, so what survives this
 * filter still costs GitHub nothing.)
 */
export function useDeployIndicator(): DeployIndicator {
	const discoveryApi = useApi(discoveryApiRef);
	const fetchApi = useApi(fetchApiRef);
	const [indicator, setIndicator] = useState<DeployIndicator>("current");

	useEffect(() => {
		let active = true;
		let timer: ReturnType<typeof setTimeout> | undefined;

		const load = async () => {
			if (!document.hidden) {
				try {
					const baseUrl = await discoveryApi.getBaseUrl(DEPLOY_PLUGIN_ID);
					const res = await fetchApi.fetch(`${baseUrl}${DEPLOY_STATUS_PATH}`);
					if (!res.ok) throw new Error(`deploy status responded ${res.status}`);
					const body = (await res.json()) as DeployStatusResponse;
					if (!active) return;
					const next = classifyDeployStatus(body);
					setIndicator(next);
					// Terminal — only a reload can clear it, and the item now says so.
					if (next === "stale") return;
				} catch {
					if (active) setIndicator("current");
				}
			}
			if (active) timer = setTimeout(load, POLL_INTERVAL_MS);
		};

		void load();
		return () => {
			active = false;
			if (timer) clearTimeout(timer);
		};
	}, [discoveryApi, fetchApi]);

	return indicator;
}
